import { supabase } from "@/lib/supabase";
import type { Database } from "@/lib/database.types";
import { fetchSubjectBySlug } from "./subjects";

export type QuestionRow = Database["public"]["Tables"]["questions"]["Row"];
export type PracticeSessionRow = Database["public"]["Tables"]["practice_sessions"]["Row"];
export type QuestionAttemptRow = Database["public"]["Tables"]["question_attempts"]["Row"];

/** Maximum questions per request - enforced both client and server side */
const MAX_QUESTIONS_PER_REQUEST = 50;

export type PracticeQuestion = {
  id: string;
  unitId: string;
  topicId: string | null;
  topicSlug: string | null;
  topicTitle: string | null;
  difficulty: "easy" | "medium" | "hard" | "unassigned" | "unknown";
  stem: string;
  choices: Array<{ text: string; reason?: string }>;
  correctIndex: number | null;
  explanation: string | null;
  kcRef: string | null;
};

function normalizeChoices(choices: unknown): Array<{ text: string; reason?: string }> {
  if (!choices) return [];
  if (Array.isArray(choices)) {
    // New format: array of objects with text and reason
    if (choices.length && typeof choices[0] === "object" && choices[0] !== null) {
      const firstItem = choices[0] as Record<string, unknown>;
      if ("text" in firstItem) {
        return (choices as Array<{ text: string; reason?: string }>).map((item) => ({
          text: item.text ?? "",
          reason: item.reason,
        }));
      }
      // Legacy format: objects with label
      if ("label" in firstItem) {
        return (choices as Array<{ label?: string }>).map((item) => ({
          text: item?.label ?? "",
        }));
      }
    }
    // Legacy format: array of strings
    if (choices.length && typeof choices[0] === "string") {
      return (choices as string[]).map((text) => ({ text }));
    }
  }
  return [];
}

export function mapQuestionRow(row: QuestionRow): PracticeQuestion {
  return {
    id: row.id,
    unitId: row.unit_id,
    topicId: row.topic_id,
    topicSlug: row.topic_slug ?? null,
    topicTitle: row.topic_title ?? null,
    difficulty: (row.difficulty as PracticeQuestion["difficulty"]) ?? "unknown",
    stem: row.stem,
    choices: normalizeChoices(row.choices),
    correctIndex: typeof row.answer_index === "number" ? row.answer_index : null,
    explanation: row.explanation ?? null,
    kcRef: row.kc_ref ?? null,
  };
}

export async function fetchAnsweredQuestionIds(userId: string, subjectId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from("question_attempts")
    .select("question_id, questions!inner(subject_id)")
    .eq("user_id", userId)
    .eq("questions.subject_id", subjectId)
    .not("answer", "is", null)
  .not("question_id", "is", null);
  if (error) throw error;
  const set = new Set<string>();
  (data ?? []).forEach((row) => {
    if (row.question_id) set.add(row.question_id);
  });
  return Array.from(set);
}

export async function fetchAnsweredQuestionsDetailed(
  userId: string,
  subjectId: string,
  limit = 100
): Promise<Array<{ id: string; createdAt: string | null; unitId: string; topicSlug: string | null }>> {
  const { data, error } = await supabase
    .from("question_attempts")
    .select("question_id, created_at, questions!inner(unit_id, topic_slug, topic_title)")
    .eq("user_id", userId)
    .eq("questions.subject_id", subjectId)
    .not("answer", "is", null)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []).map((row) => {
    const question = Array.isArray(row.questions) ? row.questions[0] : row.questions;
    return {
      id: row.question_id,
      createdAt: row.created_at,
      unitId: question?.unit_id ?? "",
      topicSlug: question?.topic_slug ?? null,
    };
  });
}

export async function clearAnsweredQuestions(userId: string, subjectId: string): Promise<void> {
  const { data, error } = await supabase
    .from("question_attempts")
    .select("id, questions!inner(subject_id)")
    .eq("user_id", userId)
    .eq("questions.subject_id", subjectId);
  if (error) throw error;
  const ids = (data ?? []).map((row) => row.id);
  if (!ids.length) return;
  const { error: deleteError } = await supabase
    .from("question_attempts")
    .delete()
    .in("id", ids);
  if (deleteError) throw deleteError;
}

export async function createPracticeSession(args: {
  userId: string;
  subjectSlug: string;
  unitIds: string[];
  topicIds: string[];
  difficulty: string;
  count: number;
}): Promise<PracticeSessionRow> {
  const subject = await fetchSubjectBySlug(args.subjectSlug);
  if (!subject) throw new Error(`Subject ${args.subjectSlug} not found`);
  const payload: Database["public"]["Tables"]["practice_sessions"]["Insert"] = {
    user_id: args.userId,
    subject_id: subject.id,
    unit_ids: args.unitIds,
    config: {
      topicIds: args.topicIds,
      difficulty: args.difficulty,
      count: args.count,
    },
  };
  const { data, error } = await supabase
    .from("practice_sessions")
    .insert(payload)
    .select("*")
    .maybeSingle();
  if (error) throw error;
  if (!data) throw new Error("Failed to create practice session");
  return data;
}

export async function fetchPracticeSession(sessionId: string): Promise<PracticeSessionRow> {
  const { data, error } = await supabase
    .from("practice_sessions")
    .select("*")
    .eq("id", sessionId)
    .maybeSingle();
  if (error) throw error;
  if (!data) throw new Error("Practice session not found");
  return data;
}

export async function fetchRandomQuestions(args: {
  subjectSlug: string;
  unitIds: string[];
  topicIds: string[];
  difficulty: string;
  count: number;
  excludeQuestionIds?: string[];
}): Promise<PracticeQuestion[]> {
  // Client-side limit enforcement (server also enforces max 50)
  const safeCount = Math.min(Math.max(args.count, 1), MAX_QUESTIONS_PER_REQUEST);

  const { data, error } = await supabase.rpc("get_random_questions", {
    p_subject_slug: args.subjectSlug,
    p_unit_ids: args.unitIds.length ? args.unitIds : null,
    p_topic_ids: args.topicIds.length ? args.topicIds : null,
    p_difficulty: args.difficulty,
    p_limit: safeCount,
    p_exclude_question_ids: args.excludeQuestionIds?.length ? args.excludeQuestionIds : null,
  });
  if (error) throw error;

  return (data ?? []).map(mapQuestionRow);
}

export type AttemptPayload = {
  questionId: string;
  selectedIndex: number | null;
  isCorrect: boolean | null;
  timeTakenSeconds?: number | null;
};

export async function recordQuestionAttempts(
  sessionId: string,
  userId: string,
  attempts: AttemptPayload[]
): Promise<QuestionAttemptRow[]> {
  if (!attempts.length) return [];
  const records: Database["public"]["Tables"]["question_attempts"]["Insert"][] = attempts.map((attempt) => ({
    session_id: sessionId,
    user_id: userId,
    question_id: attempt.questionId,
    answer: attempt.selectedIndex == null ? null : { choice: attempt.selectedIndex },
    is_correct: attempt.isCorrect,
    time_taken_seconds: attempt.timeTakenSeconds ?? null,
  }));
  const { data, error } = await supabase
    .from("question_attempts")
    .insert(records)
    .select("*");
  if (error) throw error;
  return (data ?? []).map((row) => ({
    ...row,
    questions: Array.isArray(row.questions) ? row.questions[0] : row.questions,
  }));
}

export async function completePracticeSession(
  sessionId: string,
  score: number | null
): Promise<void> {
  const { error } = await supabase
    .from("practice_sessions")
    .update({
      completed_at: new Date().toISOString(),
      score,
    })
    .eq("id", sessionId);
  if (error) throw error;
}

export async function fetchPracticeSummary(sessionId: string) {
  const { data: session, error: sessionError } = await supabase
    .from("practice_sessions")
    .select("*")
    .eq("id", sessionId)
    .maybeSingle();
  if (sessionError) throw sessionError;
  if (!session) throw new Error("Session not found");

  const { data: attempts, error: attemptsError } = await supabase
    .from("question_attempts")
    .select("*, questions(stem, unit_id, topic_slug, topic_title, answer_index, choices, kc_ref)")
    .eq("session_id", sessionId)
    .order("created_at", { ascending: true });
  if (attemptsError) throw attemptsError;

  const normalizedAttempts = (attempts ?? []).map((row) => {
    const question = Array.isArray(row.questions) ? row.questions[0] : row.questions;
    return {
      ...row,
      questions: question,
    };
  });

  return { session, attempts: normalizedAttempts };
}

export async function fetchPracticeHistory(
  userId: string,
  subjectId: string,
  limit = 20
): Promise<PracticeSessionRow[]> {
  const { data, error } = await supabase
    .from("practice_sessions")
    .select("*")
    .eq("user_id", userId)
    .eq("subject_id", subjectId)
    .order("started_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data ?? [];
}

export async function fetchPracticeHistoryForUser(
  userId: string,
  limit = 20
): Promise<PracticeSessionRow[]> {
  const { data, error } = await supabase
    .from("practice_sessions")
    .select("*")
    .eq("user_id", userId)
    .order("started_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data ?? [];
}

export async function listFlaggedQuestionIds(userId: string, subjectId?: string): Promise<string[]> {
  const query = supabase
    .from("flagged_questions")
    .select("question_id, questions!inner(subject_id)")
    .eq("user_id", userId);
  if (subjectId) {
    query.eq("questions.subject_id", subjectId);
  }
  const { data, error } = await query;
  if (error) throw error;
  const set = new Set<string>();
  (data ?? []).forEach((row) => {
    if (row.question_id) set.add(row.question_id);
  });
  return Array.from(set);
}

export async function setFlaggedQuestion(
  userId: string,
  questionId: string,
  flagged: boolean
): Promise<void> {
  if (flagged) {
    const { error } = await supabase
      .from("flagged_questions")
      .upsert({ user_id: userId, question_id: questionId });
    if (error) throw error;
    return;
  }
  const { error } = await supabase
    .from("flagged_questions")
    .delete()
    .eq("user_id", userId)
    .eq("question_id", questionId);
  if (error) throw error;
}

export async function fetchFlaggedQuestionsDetailed(userId: string, subjectId: string) {
  const { data, error } = await supabase
    .from("flagged_questions")
    .select(`
      question_id,
      created_at,
      questions!inner(
        id,
        unit_id,
        topic_id,
        topic_slug,
        topic_title,
        stem,
        choices,
        answer_index,
        explanation,
        difficulty,
        kc_ref
      )
    `)
    .eq("user_id", userId)
    .eq("questions.subject_id", subjectId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export async function clearFlaggedQuestions(userId: string, subjectId?: string): Promise<void> {
  if (subjectId) {
    // If subject filter is provided, we need to get question_ids first
    const { data, error } = await supabase
      .from("flagged_questions")
      .select("question_id, questions!inner(subject_id)")
      .eq("user_id", userId)
      .eq("questions.subject_id", subjectId);
    if (error) throw error;
    const questionIds = (data ?? []).map((row) => row.question_id).filter(Boolean);
    if (!questionIds.length) return;
    const { error: deleteError } = await supabase
      .from("flagged_questions")
      .delete()
      .eq("user_id", userId)
      .in("question_id", questionIds);
    if (deleteError) throw deleteError;
  } else {
    // No subject filter, delete all flagged questions for this user
    const { error } = await supabase
      .from("flagged_questions")
      .delete()
      .eq("user_id", userId);
    if (error) throw error;
  }
}
